import { Column } from '@ant-design/charts';
import {
  formatDate,
  formatDateTimeChart,
  useSuspendingGet,
  useTranslation
} from '@threatadvisor/react-utils';
import { ResponseMetrics } from '@threatadvisor/react-utils/src/types/generated.SecurityEvent';
import { Typography } from 'antd';
import React, { ReactElement, useId, useMemo } from 'react';
import { getPriorityScaleColors } from '../../utils';

export type AverageClientReponseTimeProps = {
  clientUid: string;
  reportStart: string;
  reportEnd: string;
  securityEventApi: string;
};

export default function AverageClientReponseTime(
  props: AverageClientReponseTimeProps
): ReactElement {
  const { securityEventApi, clientUid, reportStart, reportEnd } = props;
  const { t } = useTranslation('translation');

  const data = useSuspendingGet<ResponseMetrics[]>(
    `${securityEventApi}/report/AvgClientResponseByPriority`,
    {
      query: {
        clientUid: clientUid,
        startDate: reportStart,
        endDate: reportEnd
      }
    }
  );

  const avgMinutes: number =
    data && data !== null && data.length > 0 ? data[0].overall_avg_minutes_to_response! : 0;

  const myId = useId();
  if (!window['chartRenders']) window['chartRenders'] = {};
  window['chartRenders'][myId] = 'Rendering';

  const getData = useMemo(() => {
    if (data) {
      let res = data;

      if (!res.find(e => e.priority === 'P1'))
        res.push({
          priority: 'P1',
          total: 0,
          avg_minutes_to_response: 0,
          overall_avg_minutes_to_response: avgMinutes
        });
      if (!res.find(e => e.priority === 'P2'))
        res.push({
          priority: 'P2',
          total: 0,
          avg_minutes_to_response: 0,
          overall_avg_minutes_to_response: avgMinutes
        });
      if (!res.find(e => e.priority === 'P3'))
        res.push({
          priority: 'P3',
          total: 0,
          avg_minutes_to_response: 0,
          overall_avg_minutes_to_response: avgMinutes
        });
      if (!res.find(e => e.priority === 'P4'))
        res.push({
          priority: 'P4',
          total: 0,
          avg_minutes_to_response: 0,
          overall_avg_minutes_to_response: avgMinutes
        });
      res = res.sort((a, b) => a.priority.toLowerCase().localeCompare(b.priority.toLowerCase()));

      return res;
    }
  }, [data]);

  return (
    <>
      <h2 className="text-uppercase">Average Client Response Time</h2>

      <figure>
        {' '}
        <Typography.Title level={4}>
          Overall Average Client Response:{' '}
          {avgMinutes < 1 ? '< 1 min' : formatDateTimeChart(avgMinutes, false)}
        </Typography.Title>
      </figure>
      <h3>Average Client Response Time by Priority Level</h3>
      <figure>
        {' '}
        <Column
          onReady={plot => {
            plot.chart.on('afterrender', args => {
              if (window['chartRenders']) window['chartRenders'][myId] = 'Done';
            });
          }}
          {...{
            data: getData,
            colorField: 'priority',
            xField: 'priority',
            yField: 'avg_minutes_to_response',
            style: { maxWidth: 50 },
            legend: false,
            label: {
              // AT EACH Y POINT
              formatter: (val, itm) => formatDateTimeChart(itm.avg_minutes_to_response, false),
              text: val => (val.y === 0 ? '' : val.y),
              textBaseline: 'bottom',
              style: {
                becomeWrap: true,
                wordWrapWidth: 40,
                wordWrap: true
              }
            },
            ...getPriorityScaleColors(),
            theme: {
              type: 'threatadvisor'
            },
            tooltip: false,
            axis: {
              // ACROSS THE Y Axis... vertical in this case
              y: {
                labelFormatter: (text: string, item: any, index: number) => {
                  return formatDateTimeChart(text, false);
                }
              }
            }
          }}
        />
        <figcaption>
          <Typography.Text type="secondary">
            Shows average time to client first response between {formatDate(new Date(reportStart))}{' '}
            and {formatDate(new Date(reportEnd))}.
          </Typography.Text>
        </figcaption>
      </figure>
    </>
  );
}
